/**
 * Conteúdo editorial do site de carreiras da Guebly.
 *
 * Centraliza textos de employer branding (benefícios, valores, processo
 * seletivo, depoimentos e FAQ) para facilitar manutenção sem mexer no JSX.
 *
 * IMPORTANTE: os depoimentos em TESTIMONIALS são amostras editáveis.
 * Substitua por falas reais de pessoas do time antes de divulgar.
 */

/* Números de destaque exibidos no hero e na barra de confiança. */
export const HIGHLIGHTS = [
  { value: "6", label: "empresas no grupo" },
  { value: "25+", label: "produtos em construção" },
  { value: "100%", label: "remoto" },
  { value: "≤ 5 dias", label: "para 1ª resposta" },
];

/* Benefícios / o que oferecemos. */
export const BENEFITS = [
  {
    icon: "🌎",
    title: "100% remoto, de verdade",
    desc: "Time distribuído pelo Brasil. Trabalhe de onde render melhor — resultado importa, não CEP.",
  },
  {
    icon: "🚀",
    title: "Impacto desde o dia 1",
    desc: "Time enxuto: o que você entrega vai pra produção e é usado de verdade, sem burocracia infinita.",
  },
  {
    icon: "🧭",
    title: "Autonomia com clareza",
    desc: "Objetivos bem definidos e liberdade total para chegar lá do seu jeito.",
  },
  {
    icon: "📈",
    title: "Crescimento acelerado",
    desc: "Holding em expansão com 6 frentes. Espaço real para assumir mais responsabilidade rápido.",
  },
  {
    icon: "🛠️",
    title: "Stack moderna",
    desc: "React 19, Node.js, Supabase, visão computacional e IA aplicada. Tecnologia atual, não legado.",
  },
  {
    icon: "🤝",
    title: "Cuidado genuíno",
    desc: "Ambiente que cobra alto desempenho, mas trata bem-estar como prioridade — não como discurso.",
  },
  {
    icon: "🎓",
    title: "Aprendizado contínuo",
    desc: "Acesso a frentes diversas: fintech, contabilidade, games, edtech e segurança no mesmo grupo.",
  },
  {
    icon: "💡",
    title: "Voz no produto",
    desc: "Suas ideias chegam a quem decide. Aqui engenharia, design e produto conversam de perto.",
  },
];

/* Valores / cultura. */
export const VALUES = [
  {
    title: "Execução real",
    desc: "Preferimos software em produção a slide bonito. A entrega é a régua.",
  },
  {
    title: "Dono do problema",
    desc: "Cada pessoa assume o resultado, não só a tarefa. Ownership de ponta a ponta.",
  },
  {
    title: "Transparência",
    desc: "Contexto compartilhado, feedback direto e decisões explicadas.",
  },
  {
    title: "Origem como força",
    desc: "Nascemos numa escola pública na periferia de SP. Talento está em todo lugar — e a gente prova isso.",
  },
];

/* Etapas do processo seletivo (timeline). */
export const PROCESS = [
  {
    step: "01",
    title: "Inscrição",
    desc: "Você se candidata em poucos minutos e anexa o currículo. Sem cadastro chato.",
    meta: "5 min",
  },
  {
    step: "02",
    title: "Triagem",
    desc: "Olhamos cada candidatura com atenção e damos retorno — aprovado ou não.",
    meta: "≤ 5 dias",
  },
  {
    step: "03",
    title: "Conversa inicial",
    desc: "Bate-papo leve sobre sua trajetória, expectativas e fit com a cultura.",
    meta: "~30 min",
  },
  {
    step: "04",
    title: "Desafio técnico",
    desc: "Um desafio prático e justo, focado no dia a dia real da vaga. Sem pegadinha.",
    meta: "no seu tempo",
  },
  {
    step: "05",
    title: "Conversa final & proposta",
    desc: "Alinhamento com a liderança e, se rolar match dos dois lados, proposta.",
    meta: "decisão rápida",
  },
];

/* Depoimentos — AMOSTRAS. Substitua por falas reais do time. */
export const TESTIMONIALS = [
  {
    quote:
      "Entrei achando que ia mexer num produto só. Em poucos meses já tinha tocado coisas no Pay e no Studio. Aprendi mais aqui do que em anos no mercado.",
    name: "Pessoa do time",
    role: "Engenharia · Guebly Studio",
    initials: "GS",
    color: "#3B6FB0",
  },
  {
    quote:
      "O que me prende é a autonomia. Tenho contexto do negócio, minha opinião conta e o que eu desenho vai pro ar rápido.",
    name: "Pessoa do time",
    role: "Design de Produto",
    initials: "DP",
    color: "#7C3AED",
  },
  {
    quote:
      "É intenso e exige, mas ninguém te deixa na mão. Cultura de cobrar alto cuidando bem da pessoa — raro de achar.",
    name: "Pessoa do time",
    role: "Dados · Guebly Contábil",
    initials: "GC",
    color: "#3A6FD8",
  },
];

/* Mensagem do fundador (real). */
export const FOUNDER = {
  name: "Gabriel Pereira",
  role: "Founder & CEO — Guebly Holding",
  initials: "GP",
  photo: "/assets/team/gabriel.jpg",
  message:
    "A Guebly nasceu em 2024 dentro de uma escola pública na periferia de São Paulo, com uma ideia simples: grandes negócios podem nascer de qualquer lugar quando há visão e execução. Hoje somos uma holding com 6 frentes e 25+ produtos em construção. Se você quer construir de verdade, com autonomia e impacto real, esse é o lugar.",
};

/* Perguntas frequentes. */
export const FAQ = [
  {
    q: "As vagas são remotas?",
    a: "A maioria sim — operamos como time distribuído pelo Brasil e boa parte das vagas é home office de verdade. Mas nem todas: algumas posições são (ou virão a ser) presenciais ou híbridas. Por isso, confira sempre a \"Localidade\" de cada vaga, que indica se ela é remota, híbrida ou presencial.",
  },
  {
    q: "Em quanto tempo recebo um retorno?",
    a: "Nosso compromisso é dar a primeira resposta em até 5 dias úteis após a inscrição — aprovado para a próxima etapa ou não.",
  },
  {
    q: "Preciso ter experiência em todas as tecnologias da vaga?",
    a: "Não. Procuramos a combinação certa de fundamentos, vontade de aprender e fit cultural. Se faltar uma ferramenta específica, a gente te ajuda a evoluir.",
  },
  {
    q: "Não achei uma vaga pra mim. E agora?",
    a: "Entre no nosso banco de talentos em /banco-de-talentos. Guardamos seu perfil e chamamos quando surgir uma vaga aderente — sem precisar ficar voltando aqui.",
  },
  {
    q: "Como acompanho minha candidatura?",
    a: "Após se inscrever você recebe um código de acompanhamento por e-mail. É só usar na página \"Minha candidatura\" para ver o status em tempo real.",
  },
  {
    q: "A Guebly contrata pessoas em início de carreira?",
    a: "Sim. Acreditamos que talento está em todo lugar. Avaliamos potencial, não só currículo — e abrimos posições de diferentes níveis.",
  },
];
